import React from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Plus, Trash2, ExternalLink, ChevronUp, ChevronDown } from 'lucide-react';
import { HomePageContent } from '../../constants/homePageDefaults';
import { HERO_CAROUSEL_TYPE_LABELS, createCustomCarouselSlide } from '../../utils/heroCarouselOrder';

interface Props {
  homePage: HomePageContent;
  onChange: (homePage: HomePageContent) => void;
  onSave: () => Promise<void>;
  saving: boolean;
}

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Card className="p-6 space-y-4 border border-border bg-background">
    <h3 className="text-lg font-semibold text-secondary border-b border-border pb-2">{title}</h3>
    {children}
  </Card>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
  </div>
);

const HomePageContentEditor: React.FC<Props> = ({ homePage, onChange, onSave, saving }) => {
  const update = (partial: Partial<HomePageContent>) => {
    onChange({ ...homePage, ...partial });
  };

  return (
    <div className="space-y-6">
      <Card className="p-4 bg-blue-50 border border-blue-200">
        <p className="text-sm text-blue-900">
          Event and Hackathon carousel slides are managed separately. Enable{' '}
          <strong>Show on Home Carousel</strong> in{' '}
          <Link to="/admin/events" className="underline font-medium">Event Management</Link> or{' '}
          <Link to="/admin/hackathons" className="underline font-medium">Hackathon admin</Link>.
          Sponsor logos are edited in the Site Settings tab below.
        </p>
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-blue-700 font-medium mt-2 hover:underline"
        >
          View live home page <ExternalLink className="w-3 h-3" />
        </a>
      </Card>

      <SectionCard title="Hero Carousel — order & visibility">
        <p className="text-sm text-gray-600">
          Reorder slides, show/hide built-in slides, or add custom slides. Hackathon and Event slides only appear when items are flagged in Event/Hackathon admin.
        </p>
        {(homePage.heroCarouselOrder || []).map((slide, index) => (
          <div key={slide.id} className="flex flex-col gap-3 p-4 border border-border rounded-lg bg-gray-50/50">
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={slide.enabled !== false}
                  onChange={(e) => {
                    const heroCarouselOrder = [...(homePage.heroCarouselOrder || [])];
                    heroCarouselOrder[index] = { ...slide, enabled: e.target.checked };
                    update({ heroCarouselOrder });
                  }}
                />
                {HERO_CAROUSEL_TYPE_LABELS[slide.type] || slide.type}
              </label>
              <div className="flex gap-1 ml-auto">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={index === 0}
                  onClick={() => {
                    const heroCarouselOrder = [...(homePage.heroCarouselOrder || [])];
                    [heroCarouselOrder[index - 1], heroCarouselOrder[index]] = [heroCarouselOrder[index], heroCarouselOrder[index - 1]];
                    update({ heroCarouselOrder });
                  }}
                >
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={index === (homePage.heroCarouselOrder?.length || 0) - 1}
                  onClick={() => {
                    const heroCarouselOrder = [...(homePage.heroCarouselOrder || [])];
                    [heroCarouselOrder[index + 1], heroCarouselOrder[index]] = [heroCarouselOrder[index], heroCarouselOrder[index + 1]];
                    update({ heroCarouselOrder });
                  }}
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
                {slide.type === 'custom' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const heroCarouselOrder = (homePage.heroCarouselOrder || []).filter((_, i) => i !== index);
                      update({ heroCarouselOrder });
                    }}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            </div>
            {slide.type === 'custom' && slide.custom && (
              <div className="grid md:grid-cols-2 gap-3 pl-1">
                <Field label="Title">
                  <Input
                    value={slide.custom.title}
                    onChange={(e) => {
                      const heroCarouselOrder = [...(homePage.heroCarouselOrder || [])];
                      heroCarouselOrder[index] = {
                        ...slide,
                        custom: { ...slide.custom!, title: e.target.value },
                      };
                      update({ heroCarouselOrder });
                    }}
                    placeholder="Get [[YOUR RESUME]] Picked"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Wrap words in [[double brackets]] to highlight in orange.
                  </p>
                </Field>
                <Field label="Style">
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={slide.custom.variant || 'default'}
                    onChange={(e) => {
                      const heroCarouselOrder = [...(homePage.heroCarouselOrder || [])];
                      heroCarouselOrder[index] = {
                        ...slide,
                        custom: {
                          ...slide.custom!,
                          variant: e.target.value as 'default' | 'featured',
                        },
                      };
                      update({ heroCarouselOrder });
                    }}
                  >
                    <option value="default">Default (plain text)</option>
                    <option value="featured">Featured (gradient card + badge)</option>
                  </select>
                </Field>
                <Field label="Badge (featured style only)">
                  <Input
                    value={slide.custom.badge || ''}
                    onChange={(e) => {
                      const heroCarouselOrder = [...(homePage.heroCarouselOrder || [])];
                      heroCarouselOrder[index] = {
                        ...slide,
                        custom: { ...slide.custom!, badge: e.target.value },
                      };
                      update({ heroCarouselOrder });
                    }}
                    placeholder="e.g. Resume AI"
                  />
                </Field>
                <Field label="Subtitle">
                  <Input
                    value={slide.custom.subtitle}
                    onChange={(e) => {
                      const heroCarouselOrder = [...(homePage.heroCarouselOrder || [])];
                      heroCarouselOrder[index] = {
                        ...slide,
                        custom: { ...slide.custom!, subtitle: e.target.value },
                      };
                      update({ heroCarouselOrder });
                    }}
                  />
                </Field>
                <Field label="Primary button label">
                  <Input
                    value={slide.custom.primaryCta}
                    onChange={(e) => {
                      const heroCarouselOrder = [...(homePage.heroCarouselOrder || [])];
                      heroCarouselOrder[index] = {
                        ...slide,
                        custom: { ...slide.custom!, primaryCta: e.target.value },
                      };
                      update({ heroCarouselOrder });
                    }}
                  />
                </Field>
                <Field label="Primary button link">
                  <Input
                    value={slide.custom.primaryCtaLink}
                    onChange={(e) => {
                      const heroCarouselOrder = [...(homePage.heroCarouselOrder || [])];
                      heroCarouselOrder[index] = {
                        ...slide,
                        custom: { ...slide.custom!, primaryCtaLink: e.target.value },
                      };
                      update({ heroCarouselOrder });
                    }}
                    placeholder="/register or /#resume-ai-analysis"
                  />
                </Field>
                <Field label="Secondary button label">
                  <Input
                    value={slide.custom.secondaryCta}
                    onChange={(e) => {
                      const heroCarouselOrder = [...(homePage.heroCarouselOrder || [])];
                      heroCarouselOrder[index] = {
                        ...slide,
                        custom: { ...slide.custom!, secondaryCta: e.target.value },
                      };
                      update({ heroCarouselOrder });
                    }}
                  />
                </Field>
                <Field label="Secondary button link">
                  <Input
                    value={slide.custom.secondaryCtaLink}
                    onChange={(e) => {
                      const heroCarouselOrder = [...(homePage.heroCarouselOrder || [])];
                      heroCarouselOrder[index] = {
                        ...slide,
                        custom: { ...slide.custom!, secondaryCtaLink: e.target.value },
                      };
                      update({ heroCarouselOrder });
                    }}
                    placeholder="/events"
                  />
                </Field>
              </div>
            )}
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            update({
              heroCarouselOrder: [...(homePage.heroCarouselOrder || []), createCustomCarouselSlide()],
            })
          }
        >
          <Plus className="w-4 h-4 mr-1" /> Add custom carousel slide
        </Button>
      </SectionCard>

      <SectionCard title="News Ticker">
        {homePage.newsTicker.map((item, index) => (
          <div key={index} className="flex gap-2 items-start">
            <Textarea
              value={item.text}
              onChange={(e) => {
                const newsTicker = [...homePage.newsTicker];
                newsTicker[index] = { text: e.target.value };
                update({ newsTicker });
              }}
              className="flex-1 min-h-[60px]"
              placeholder="Ticker message"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled={homePage.newsTicker.length <= 1}
              onClick={() => update({ newsTicker: homePage.newsTicker.filter((_, i) => i !== index) })}
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        ))}
        {homePage.newsTicker.length < 10 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => update({ newsTicker: [...homePage.newsTicker, { text: '' }] })}
          >
            <Plus className="w-4 h-4 mr-1" /> Add message
          </Button>
        )}
      </SectionCard>

      <SectionCard title="Hero Slide — Empowering Careers">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Title line 1">
            <Input
              value={homePage.heroSlide.titleLine1}
              onChange={(e) => update({ heroSlide: { ...homePage.heroSlide, titleLine1: e.target.value } })}
            />
          </Field>
          <Field label="Title line 2 (highlighted)">
            <Input
              value={homePage.heroSlide.titleLine2}
              onChange={(e) => update({ heroSlide: { ...homePage.heroSlide, titleLine2: e.target.value } })}
            />
          </Field>
        </div>
        <Field label="Subtitle">
          <Textarea
            value={homePage.heroSlide.subtitle}
            onChange={(e) => update({ heroSlide: { ...homePage.heroSlide, subtitle: e.target.value } })}
          />
        </Field>
      </SectionCard>

      <SectionCard title="Hero Slide — Success Stories">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Slide title">
            <Input
              value={homePage.successStoriesSlide.title}
              onChange={(e) => update({ successStoriesSlide: { ...homePage.successStoriesSlide, title: e.target.value } })}
            />
          </Field>
          <Field label="Count (large number)">
            <Input
              value={homePage.successStoriesSlide.count}
              onChange={(e) => update({ successStoriesSlide: { ...homePage.successStoriesSlide, count: e.target.value } })}
            />
          </Field>
        </div>
        <Field label="Headline">
          <Input
            value={homePage.successStoriesSlide.headline}
            onChange={(e) => update({ successStoriesSlide: { ...homePage.successStoriesSlide, headline: e.target.value } })}
          />
        </Field>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Salary prefix">
            <Input
              value={homePage.successStoriesSlide.salaryPrefix}
              onChange={(e) => update({ successStoriesSlide: { ...homePage.successStoriesSlide, salaryPrefix: e.target.value } })}
            />
          </Field>
          <Field label="Salary range (highlighted)">
            <Input
              value={homePage.successStoriesSlide.salaryRangeText}
              onChange={(e) => update({ successStoriesSlide: { ...homePage.successStoriesSlide, salaryRangeText: e.target.value } })}
            />
          </Field>
          <Field label="Salary suffix">
            <Input
              value={homePage.successStoriesSlide.salarySuffix}
              onChange={(e) => update({ successStoriesSlide: { ...homePage.successStoriesSlide, salarySuffix: e.target.value } })}
            />
          </Field>
        </div>
        <Field label="Description">
          <Textarea
            value={homePage.successStoriesSlide.description}
            onChange={(e) => update({ successStoriesSlide: { ...homePage.successStoriesSlide, description: e.target.value } })}
          />
        </Field>
        {homePage.successStoriesSlide.stats.map((stat, index) => (
          <div key={index} className="grid md:grid-cols-2 gap-4">
            <Field label={`Stat ${index + 1} value`}>
              <Input
                value={stat.value}
                onChange={(e) => {
                  const stats = [...homePage.successStoriesSlide.stats];
                  stats[index] = { ...stats[index], value: e.target.value };
                  update({ successStoriesSlide: { ...homePage.successStoriesSlide, stats } });
                }}
              />
            </Field>
            <Field label={`Stat ${index + 1} label`}>
              <Input
                value={stat.label}
                onChange={(e) => {
                  const stats = [...homePage.successStoriesSlide.stats];
                  stats[index] = { ...stats[index], label: e.target.value };
                  update({ successStoriesSlide: { ...homePage.successStoriesSlide, stats } });
                }}
              />
            </Field>
          </div>
        ))}
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Primary button label">
            <Input
              value={homePage.successStoriesSlide.primaryCta}
              onChange={(e) => update({ successStoriesSlide: { ...homePage.successStoriesSlide, primaryCta: e.target.value } })}
            />
          </Field>
          <Field label="Secondary button label">
            <Input
              value={homePage.successStoriesSlide.secondaryCta}
              onChange={(e) => update({ successStoriesSlide: { ...homePage.successStoriesSlide, secondaryCta: e.target.value } })}
            />
          </Field>
        </div>
      </SectionCard>

      <SectionCard title="Hero Slide — Expert Mentors (headers only)">
        <Field label="Slide title">
          <Input
            value={homePage.mentorsSlide.title}
            onChange={(e) => update({ mentorsSlide: { ...homePage.mentorsSlide, title: e.target.value } })}
          />
        </Field>
        <Field label="Subtitle">
          <Textarea
            value={homePage.mentorsSlide.subtitle}
            onChange={(e) => update({ mentorsSlide: { ...homePage.mentorsSlide, subtitle: e.target.value } })}
          />
        </Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Empty state title">
            <Input
              value={homePage.mentorsSlide.emptyTitle}
              onChange={(e) => update({ mentorsSlide: { ...homePage.mentorsSlide, emptyTitle: e.target.value } })}
            />
          </Field>
          <Field label="Empty state subtitle">
            <Input
              value={homePage.mentorsSlide.emptySubtitle}
              onChange={(e) => update({ mentorsSlide: { ...homePage.mentorsSlide, emptySubtitle: e.target.value } })}
            />
          </Field>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Primary button label">
            <Input
              value={homePage.mentorsSlide.primaryCta}
              onChange={(e) => update({ mentorsSlide: { ...homePage.mentorsSlide, primaryCta: e.target.value } })}
            />
          </Field>
          <Field label="Secondary button label">
            <Input
              value={homePage.mentorsSlide.secondaryCta}
              onChange={(e) => update({ mentorsSlide: { ...homePage.mentorsSlide, secondaryCta: e.target.value } })}
            />
          </Field>
        </div>
      </SectionCard>

      <SectionCard title="Trust Indicators">
        {homePage.trustIndicators.map((item, index) => (
          <div key={index} className="grid md:grid-cols-2 gap-4">
            <Field label={`Item ${index + 1} value`}>
              <Input
                value={item.value}
                onChange={(e) => {
                  const trustIndicators = [...homePage.trustIndicators];
                  trustIndicators[index] = { ...trustIndicators[index], value: e.target.value };
                  update({ trustIndicators });
                }}
              />
            </Field>
            <Field label={`Item ${index + 1} label`}>
              <Input
                value={item.label}
                onChange={(e) => {
                  const trustIndicators = [...homePage.trustIndicators];
                  trustIndicators[index] = { ...trustIndicators[index], label: e.target.value };
                  update({ trustIndicators });
                }}
              />
            </Field>
          </div>
        ))}
      </SectionCard>

      <SectionCard title="Why Choose Udyoga Pramoda?">
        <Field label="Section title">
          <Input
            value={homePage.valueProposition.title}
            onChange={(e) => update({ valueProposition: { ...homePage.valueProposition, title: e.target.value } })}
          />
        </Field>
        <Field label="Section subtitle">
          <Textarea
            value={homePage.valueProposition.subtitle}
            onChange={(e) => update({ valueProposition: { ...homePage.valueProposition, subtitle: e.target.value } })}
          />
        </Field>
        {homePage.valueProposition.cards.map((card, index) => (
          <div key={index} className="p-4 border border-border rounded-lg space-y-3">
            <p className="text-sm font-medium text-gray-500">Card {index + 1}</p>
            <Field label="Title">
              <Input
                value={card.title}
                onChange={(e) => {
                  const cards = [...homePage.valueProposition.cards];
                  cards[index] = { ...cards[index], title: e.target.value };
                  update({ valueProposition: { ...homePage.valueProposition, cards } });
                }}
              />
            </Field>
            <Field label="Description">
              <Textarea
                value={card.description}
                onChange={(e) => {
                  const cards = [...homePage.valueProposition.cards];
                  cards[index] = { ...cards[index], description: e.target.value };
                  update({ valueProposition: { ...homePage.valueProposition, cards } });
                }}
              />
            </Field>
          </div>
        ))}
      </SectionCard>

      <SectionCard title="Testimonials">
        <Field label="Section title">
          <Input
            value={homePage.testimonials.title}
            onChange={(e) => update({ testimonials: { ...homePage.testimonials, title: e.target.value } })}
          />
        </Field>
        {homePage.testimonials.items.map((item, index) => (
          <div key={index} className="p-4 border border-border rounded-lg space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-sm font-medium text-gray-500">Testimonial {index + 1}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={homePage.testimonials.items.length <= 1}
                onClick={() =>
                  update({
                    testimonials: {
                      ...homePage.testimonials,
                      items: homePage.testimonials.items.filter((_, i) => i !== index),
                    },
                  })
                }
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
            <Field label="Quote">
              <Textarea
                value={item.quote}
                onChange={(e) => {
                  const items = [...homePage.testimonials.items];
                  items[index] = { ...items[index], quote: e.target.value };
                  update({ testimonials: { ...homePage.testimonials, items } });
                }}
                className="min-h-[100px]"
              />
            </Field>
            <Field label="Author">
              <Input
                value={item.author}
                onChange={(e) => {
                  const items = [...homePage.testimonials.items];
                  items[index] = { ...items[index], author: e.target.value };
                  update({ testimonials: { ...homePage.testimonials, items } });
                }}
              />
            </Field>
          </div>
        ))}
        {homePage.testimonials.items.length < 8 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              update({
                testimonials: {
                  ...homePage.testimonials,
                  items: [...homePage.testimonials.items, { quote: '', author: '' }],
                },
              })
            }
          >
            <Plus className="w-4 h-4 mr-1" /> Add testimonial
          </Button>
        )}
      </SectionCard>

      <SectionCard title="Sponsors Section (headings only)">
        <Field label="Title">
          <Input
            value={homePage.sponsorsSection.title}
            onChange={(e) => update({ sponsorsSection: { ...homePage.sponsorsSection, title: e.target.value } })}
          />
        </Field>
        <Field label="Subtitle">
          <Textarea
            value={homePage.sponsorsSection.subtitle}
            onChange={(e) => update({ sponsorsSection: { ...homePage.sponsorsSection, subtitle: e.target.value } })}
          />
        </Field>
        <Field label="Footer note">
          <Input
            value={homePage.sponsorsSection.footerNote}
            onChange={(e) => update({ sponsorsSection: { ...homePage.sponsorsSection, footerNote: e.target.value } })}
          />
        </Field>
      </SectionCard>

      <SectionCard title="Final Call to Action">
        <Field label="Title">
          <Input
            value={homePage.finalCta.title}
            onChange={(e) => update({ finalCta: { ...homePage.finalCta, title: e.target.value } })}
          />
        </Field>
        <Field label="Subtitle">
          <Textarea
            value={homePage.finalCta.subtitle}
            onChange={(e) => update({ finalCta: { ...homePage.finalCta, subtitle: e.target.value } })}
          />
        </Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Primary button label">
            <Input
              value={homePage.finalCta.primaryCta}
              onChange={(e) => update({ finalCta: { ...homePage.finalCta, primaryCta: e.target.value } })}
            />
          </Field>
          <Field label="Secondary button label">
            <Input
              value={homePage.finalCta.secondaryCta}
              onChange={(e) => update({ finalCta: { ...homePage.finalCta, secondaryCta: e.target.value } })}
            />
          </Field>
        </div>
      </SectionCard>

      <SectionCard title="Carousel Button Labels">
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="View all hackathons">
            <Input
              value={homePage.carouselLabels.viewAllHackathons}
              onChange={(e) => update({ carouselLabels: { ...homePage.carouselLabels, viewAllHackathons: e.target.value } })}
            />
          </Field>
          <Field label="View all events">
            <Input
              value={homePage.carouselLabels.viewAllEvents}
              onChange={(e) => update({ carouselLabels: { ...homePage.carouselLabels, viewAllEvents: e.target.value } })}
            />
          </Field>
          <Field label="Learn more">
            <Input
              value={homePage.carouselLabels.learnMore}
              onChange={(e) => update({ carouselLabels: { ...homePage.carouselLabels, learnMore: e.target.value } })}
            />
          </Field>
        </div>
      </SectionCard>

      <div className="flex justify-end pt-4 border-t border-border">
        <Button
          onClick={onSave}
          disabled={saving}
          className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-8"
        >
          {saving ? 'Saving Home Page...' : 'Save Home Page'}
        </Button>
      </div>
    </div>
  );
};

export default HomePageContentEditor;
